import { useWeb3React } from '@web3-react/core';
import { useGetContract } from './useGetContract';
import { ContractNames, PROGRAM_IDS, PROGRAM_PRICES_WEI, DEFAULT_GAS_LIMIT, ZERO_ADDRESS, PUBLIC_URLS } from '../constants';
import { useCallback, useState } from 'react';
import { Contract } from '@ethersproject/contracts';
import { JsonRpcProvider } from '@ethersproject/providers';
import config from '../config';
import { increaseByPercent } from '../numbers';
import { callNotification } from '../notification';
import { parseErrorToUserReadableMessage } from '../format';
import { useApprove } from './useApprove';
import { bigIntToInt } from '../numbers';
import useTokenPrice from './useTokenPrice';

// Временный адрес токена для dev режима
const DEV_TOKEN_ADDRESS = '0xb3457705a8ef5ab71fbe66c6d2cb146799accd0a';

let publicProviderCache = null;

export const getPublicRpcProvider = () => {
  if (publicProviderCache) {
    return publicProviderCache;
  }

  const rpcUrl = PUBLIC_URLS?.[config.allowedChainId];
  if (!rpcUrl) {
    return null;
  }

  publicProviderCache = new JsonRpcProvider(rpcUrl, Number(config.allowedChainId));
  return publicProviderCache;
};

export const usePassiveIncome = () => {
  const { account, provider } = useWeb3React();
  const { getContract } = useGetContract();
  const { fetchTokenAmount } = useTokenPrice();

  const [nextDistributionTime, setNextDistributionTime] = useState(null);
  const [passiveData, setPassiveData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingActivation, setIsLoadingActivation] = useState(false);
  const [isLoadingClaim, setIsLoadingClaim] = useState(false);

  // Для dev режима - approve токена для Income Router
  const incomeRouterAddress = config.contractIncomeRouter;
  const { isApproved, checkApprove, callApprove, ensureApprove, isLoadingApprove } = useApprove(
    DEV_TOKEN_ADDRESS,
    incomeRouterAddress,
  );

  const getReadProvider = (readProvider) => {
    if (readProvider) {
      return readProvider;
    }

    if (account && provider) {
      return provider.getSigner(account).connectUnchecked();
    }

    if (provider) {
      return provider;
    }

    return getPublicRpcProvider();
  };

  const getPassiveData = useCallback(
    async (userAddress, readProvider) => {
      const load = async (overrideProvider) => {
        const signerOrProvider = getReadProvider(overrideProvider);

        if (!signerOrProvider) {
          throw new Error('No provider for income contract');
        }

        const contract = await getContract(ContractNames.INCOME_ROUTER, signerOrProvider);
        const resultX2 = await contract.incomes(0);
        const resultX4 = await contract.incomes(1);

        if (!resultX2 || !resultX4) {
          throw new Error('Income contracts are not set');
        }

        const [passiveDataX2, passiveDataX4] = await Promise.allSettled([
          getPassiveIncome(resultX2, userAddress, overrideProvider),
          getPassiveIncome(resultX4, userAddress, overrideProvider),
        ]);

        setPassiveData({
          x2:
            passiveDataX2.status === 'fulfilled'
              ? { ...passiveDataX2.value, contractAddress: resultX2 }
              : { contractAddress: resultX2 },
          x4:
            passiveDataX4.status === 'fulfilled'
              ? { ...passiveDataX4.value, contractAddress: resultX4 }
              : { contractAddress: resultX4 },
        });
      };

      setIsLoading(true);
      try {
        await load(readProvider);
      } catch (error) {
        const publicProvider = getPublicRpcProvider();
        if (publicProvider && readProvider !== publicProvider) {
          try {
            await load(publicProvider);
            return;
          } catch (fallbackError) {
            console.log(fallbackError);
          }
        } else {
          console.log(error);
        }
        setPassiveData(null);
      } finally {
        setIsLoading(false);
      }
    },
    [account, provider, getContract],
  );

  const getPassiveIncome = async (contractAddress, userAddress, readProvider) => {
    const signerOrProvider = getReadProvider(readProvider);

    if (!signerOrProvider) {
      return Promise.reject(new Error('No provider for income contract'));
    }

    try {
      const contract = new Contract(contractAddress, config.contractIncomeAbi, signerOrProvider);
      const address = userAddress || ZERO_ADDRESS;

      const result = await contract.getCombinedClaimData(address);
      const result2 = await contract.userLastActiveLevel(address);
      const l3list = await contract.getL3ChainCurrent();
      const nextDistributionTime = await contract.nextDistributionTime();
      const timestampMs = new Date(nextDistributionTime * 1000);
      setNextDistributionTime(new Date(timestampMs));

      const toTokenAmount = (value) => {
        if (value == null) return 0;
        const raw = typeof value.toString === 'function' ? value.toString() : value;
        const number = Number(raw);
        return Number.isFinite(number) ? number / 1e18 : 0;
      };

      return {
        passiveData: {
          totalClaimed: toTokenAmount(result.totalClaimed),
          l1: toTokenAmount(result.l1),
          maxL2Reward: toTokenAmount(result.maxL2Reward),
          realL2Reward: toTokenAmount(result.realL2Reward),
          l3: toTokenAmount(result.l3),
          unclaimed: toTokenAmount(result.unclaimed),
        },
        top30: l3list,
        lastActiveLevel: Number(result2?.toString?.() ?? result2) || 0,
        time: timestampMs,
      };
    } catch (error) {
      return Promise.reject(error);
    }
  };

  const activatePassive = async (program, level) => {
    if (!isLoadingActivation) {
      setIsLoadingActivation(true);

      const programId = PROGRAM_IDS[program];
      const basePrice = BigInt(PROGRAM_PRICES_WEI[program][level - 1]);
      const tokenAmount = (basePrice * BigInt(20)) / BigInt(100);
      const minPollySwapped = await fetchTokenAmount(config.contractPollyPool, provider, bigIntToInt(tokenAmount));
      try {
        const contract = await getContract(ContractNames.ROUTER);

        let gasEstimate = 0;
        try {
          gasEstimate = await contract.estimateGas._activateIncomeLevel(programId, account, level, minPollySwapped, {
            value: tokenAmount,
          });
        } catch (e) {
          console.log('Gas estimation failed:', e);
        }

        return await contract._activateIncomeLevel(programId, account, level, BigInt(Math.floor(minPollySwapped)), {
          value: tokenAmount,
          gasLimit: parseInt(gasEstimate) ? increaseByPercent(gasEstimate) : DEFAULT_GAS_LIMIT,
        });
      } catch (error) {
        console.log(error);
        callNotification({ type: 'error', message: parseErrorToUserReadableMessage(error) });
        return Promise.reject(error);
      } finally {
        setIsLoadingActivation(false);
      }
    }
  };

  const claimPassive = async (incomeContractAddress) => {
    if (!isLoadingClaim) {
      setIsLoadingClaim(true);
      try {
        if (!incomeContractAddress || typeof incomeContractAddress !== 'string') {
          return Promise.reject(new Error('Income contract address is not set'));
        }
        const incomeContract = new Contract(
          incomeContractAddress,
          config.contractIncomeAbi,
          provider?.getSigner(account).connectUnchecked() || provider,
        );

        let gasEstimate = 0;
        try {
          gasEstimate = await incomeContract.estimateGas.claim();
        } catch (e) {
          console.log('Gas estimation failed:', e);
        }

        return await incomeContract.claim({
          gasLimit: parseInt(gasEstimate) ? increaseByPercent(gasEstimate) : DEFAULT_GAS_LIMIT,
        });
      } catch (error) {
        console.log(error);
        callNotification({ type: 'error', message: parseErrorToUserReadableMessage(error) });
        return Promise.reject(error);
      } finally {
        setIsLoadingClaim(false);
      }
    }
  };

  return {
    distributionTime: nextDistributionTime,
    getPassiveData,
    passiveData,
    isLoading,
    activatePassive,
    claimPassive,
    isLoadingActivation,
    isLoadingClaim,
    // Для работы с approve в dev режиме
    isApproved,
    checkApprove,
    callApprove,
    ensureApprove,
    isLoadingApprove,
  };
};
